class SearchForm extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');

    if (this.input?.form) {
      this.input.form.addEventListener('reset', this.onFormReset.bind(this));
      this.input.addEventListener(
        'input',
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this)
      );
    }
  }

  toggleResetButton() {
    if (!this.resetButton || !this.input) return;

    const resetIsHidden = this.resetButton.classList.contains('hidden');
    if (this.input.value.length > 0 && resetIsHidden) {
      this.resetButton.classList.remove('hidden');
    } else if (this.input.value.length === 0 && !resetIsHidden) {
      this.resetButton.classList.add('hidden');
    }
  }

  onChange() {
    this.toggleResetButton();
  }

  shouldResetForm() {
    return !document.querySelector('[aria-selected="true"] a');
  }

  onFormReset(event) {
    event.preventDefault();

    if (this.shouldResetForm() && this.input) {
      this.input.value = '';
      this.input.focus();
      this.toggleResetButton();
    }
  }
}

if (!customElements.get('search-form')) {
  customElements.define('search-form', SearchForm);
}

(() => {
  const styleId = 'summit-search-selection-style';
  const markerAttribute = 'data-summit-search-selection-fix';
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  const installedInputs = new WeakSet();
  let activeDrag = null;
  let lockedSelection = null;
  let observerScheduled = false;
  let applyingSelection = false;

  function focusInput(input) {
    try {
      input.focus({ preventScroll: true });
    } catch (error) {
      input.focus();
    }
  }

  function isCandidate(input) {
    if (!(input instanceof HTMLInputElement)) return false;

    const type = (input.type || '').toLowerCase();
    if (type !== 'search' && type !== 'text') return false;

    const appAncestor = input.closest('[class*="snize"], [id*="snize"]');
    const identifyingText = [
      input.id,
      typeof input.className === 'string' ? input.className : '',
      input.name,
      input.getAttribute('role'),
      appAncestor?.id,
      typeof appAncestor?.className === 'string' ? appAncestor.className : '',
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    return Boolean(
      type === 'search' ||
        input.name === 'q' ||
        input.closest('.section-header, .shopify-section-header, .desktop-ver, .mobile-ver, header') ||
        identifyingText.includes('snize')
    );
  }

  function getCandidates() {
    return Array.from(
      document.querySelectorAll('input[type="search"], input[type="text"], input[name="q"]')
    ).filter(isCandidate);
  }

  function installSelectionStyle() {
    if (!document.head || document.getElementById(styleId)) return;

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      input[${markerAttribute}="true"] {
        -webkit-user-select: text !important;
        user-select: text !important;
        cursor: text !important;
        touch-action: manipulation !important;
      }
      input[${markerAttribute}="true"]::selection {
        background: #b7d7ff !important;
        color: #111111 !important;
      }
      input[${markerAttribute}="true"]::-moz-selection {
        background: #b7d7ff !important;
        color: #111111 !important;
      }
    `;
    document.head.appendChild(style);
  }

  function caretIndexFromPointer(input, clientX) {
    const text = input.value || '';
    if (!context || text.length === 0) return 0;

    const style = getComputedStyle(input);
    const rect = input.getBoundingClientRect();
    context.font = [
      style.fontStyle,
      style.fontVariant,
      style.fontWeight,
      style.fontSize,
      style.fontFamily,
    ].join(' ');

    const paddingLeft = Number.parseFloat(style.paddingLeft) || 0;
    const letterSpacing = Number.parseFloat(style.letterSpacing) || 0;
    const pointerX = Math.max(0, clientX - rect.left - paddingLeft + input.scrollLeft);

    function measuredWidth(characterCount) {
      if (characterCount <= 0) return 0;
      return context.measureText(text.slice(0, characterCount)).width + letterSpacing * Math.max(0, characterCount - 1);
    }

    let low = 0;
    let high = text.length;

    while (low < high) {
      const middle = Math.floor((low + high) / 2);
      if (measuredWidth(middle) < pointerX) {
        low = middle + 1;
      } else {
        high = middle;
      }
    }

    if (low > 0) {
      const previousWidth = measuredWidth(low - 1);
      const currentWidth = measuredWidth(low);
      if (Math.abs(pointerX - previousWidth) < Math.abs(currentWidth - pointerX)) {
        return low - 1;
      }
    }

    return Math.min(low, text.length);
  }

  function applySelection(selection) {
    if (!selection?.input?.isConnected) return;

    const { input, anchor, end } = selection;
    const start = Math.min(anchor, end);
    const finish = Math.max(anchor, end);
    const direction = end >= anchor ? 'forward' : 'backward';

    applyingSelection = true;
    focusInput(input);
    try {
      input.setSelectionRange(start, finish, direction);
    } catch (error) {
      input.select();
    }
    applyingSelection = false;
  }

  function lockSelection(selection) {
    lockedSelection = {
      input: selection.input,
      anchor: selection.anchor,
      end: selection.end,
      expiresAt: performance.now() + 800,
    };

    const reapply = () => {
      if (!lockedSelection || performance.now() > lockedSelection.expiresAt) return;
      applySelection(lockedSelection);
    };

    reapply();
    queueMicrotask(reapply);
    requestAnimationFrame(reapply);
    [0, 20, 60, 140, 300, 600].forEach((delay) => window.setTimeout(reapply, delay));
  }

  function finishPointerDrag(event, cancelled = false) {
    if (!activeDrag) return;
    if (event.pointerId != null && activeDrag.pointerId != null && event.pointerId !== activeDrag.pointerId) return;

    const selection = activeDrag;
    activeDrag = null;

    try {
      if (selection.input.hasPointerCapture?.(selection.pointerId)) {
        selection.input.releasePointerCapture(selection.pointerId);
      }
    } catch (error) {
      // Pointer capture can already be released by the browser.
    }

    if (selection.moved && !cancelled) {
      event.preventDefault();
      event.stopImmediatePropagation();
      lockSelection(selection);
    }
  }

  function installInputHandlers(input) {
    if (installedInputs.has(input)) return;
    installedInputs.add(input);
    input.setAttribute(markerAttribute, 'true');

    input.addEventListener(
      'keydown',
      (event) => {
        if (!(event.ctrlKey || event.metaKey) || event.key.toLowerCase() !== 'a') return;

        event.preventDefault();
        event.stopImmediatePropagation();
        const selection = { input, anchor: 0, end: input.value.length };
        applySelection(selection);
        lockSelection(selection);
      },
      true
    );

    input.addEventListener(
      'pointerdown',
      (event) => {
        if (event.button !== 0 || event.pointerType === 'touch') return;

        const anchor = caretIndexFromPointer(input, event.clientX);
        activeDrag = {
          input,
          pointerId: event.pointerId,
          anchor,
          end: anchor,
          startX: event.clientX,
          startY: event.clientY,
          moved: false,
        };
        lockedSelection = null;

        focusInput(input);
        input.setSelectionRange(anchor, anchor);
        try {
          input.setPointerCapture(event.pointerId);
        } catch (error) {
          // Pointer capture is optional; document listeners remain as a fallback.
        }

        event.preventDefault();
        event.stopImmediatePropagation();
      },
      true
    );

    input.addEventListener(
      'pointermove',
      (event) => {
        if (!activeDrag || activeDrag.input !== input || event.pointerId !== activeDrag.pointerId) return;
        if (!(event.buttons & 1)) {
          finishPointerDrag(event);
          return;
        }

        const distance = Math.hypot(event.clientX - activeDrag.startX, event.clientY - activeDrag.startY);
        if (!activeDrag.moved && distance < 2) return;

        activeDrag.moved = true;
        activeDrag.end = caretIndexFromPointer(input, event.clientX);
        event.preventDefault();
        event.stopImmediatePropagation();
        applySelection(activeDrag);
      },
      true
    );

    input.addEventListener(
      'pointerup',
      (event) => finishPointerDrag(event),
      true
    );

    input.addEventListener(
      'pointercancel',
      (event) => finishPointerDrag(event, true),
      true
    );

    input.addEventListener(
      'lostpointercapture',
      (event) => {
        if (activeDrag?.input === input) finishPointerDrag(event);
      },
      true
    );

    input.addEventListener(
      'selectstart',
      (event) => {
        if (activeDrag?.input !== input) return;
        event.preventDefault();
        event.stopImmediatePropagation();
      },
      true
    );

    input.addEventListener(
      'dragstart',
      (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();
      },
      true
    );

    input.addEventListener(
      'click',
      (event) => {
        if (!lockedSelection || lockedSelection.input !== input || performance.now() > lockedSelection.expiresAt) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        applySelection(lockedSelection);
      },
      true
    );
  }

  function markInputs() {
    installSelectionStyle();
    const inputs = getCandidates();

    inputs.forEach((input) => {
      input.setAttribute(markerAttribute, 'true');
      installInputHandlers(input);
    });

    return inputs;
  }

  document.addEventListener(
    'selectionchange',
    () => {
      if (applyingSelection || !lockedSelection) return;
      if (performance.now() > lockedSelection.expiresAt) {
        lockedSelection = null;
        return;
      }

      const { input, anchor, end } = lockedSelection;
      if (document.activeElement !== input) return;

      const expectedStart = Math.min(anchor, end);
      const expectedEnd = Math.max(anchor, end);
      if (input.selectionStart === expectedStart && input.selectionEnd === expectedEnd) return;

      queueMicrotask(() => applySelection(lockedSelection));
    },
    true
  );

  window.addEventListener(
    'pointermove',
    (event) => {
      if (!activeDrag || event.pointerId !== activeDrag.pointerId || activeDrag.input.hasPointerCapture?.(event.pointerId)) return;
      if (!(event.buttons & 1)) {
        finishPointerDrag(event);
        return;
      }

      const distance = Math.hypot(event.clientX - activeDrag.startX, event.clientY - activeDrag.startY);
      if (!activeDrag.moved && distance < 2) return;

      activeDrag.moved = true;
      activeDrag.end = caretIndexFromPointer(activeDrag.input, event.clientX);
      event.preventDefault();
      event.stopImmediatePropagation();
      applySelection(activeDrag);
    },
    true
  );

  window.addEventListener(
    'pointerup',
    (event) => finishPointerDrag(event),
    true
  );

  function run() {
    markInputs();
  }

  function scheduleObserverRun() {
    if (observerScheduled) return;
    observerScheduled = true;

    requestAnimationFrame(() => {
      observerScheduled = false;
      run();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }

  window.addEventListener(
    'load',
    () => {
      run();
      [250, 1000, 2500, 5000].forEach((delay) => window.setTimeout(run, delay));

      const observer = new MutationObserver(scheduleObserverRun);
      observer.observe(document.documentElement, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'id', 'type', 'name'],
      });
    },
    { once: true }
  );
})();
